/**
 * Shared logic для статус-бейджей.
 *
 * badgeColors — маппинг id статуса → цвет UBadge из Nuxt UI
 * getBadgeColor(id) — функция для единообразного получения цвета
 * findStatusCol(columns) — поиск колонки статуса в массиве колонок
 * useStatusBadge(item, columns) — реактивный computed для label/id/color
 *
 * Объединяет разрозненные badgeColors из:
 *   components/table/mobile/card/index.vue
 *   components/table/table-card/index.vue
 *   components/supply/current/supply-header/index.vue
 */

// Маппинг id статуса → цвет UBadge
// Ключи — это id статуса, который приходит с API (status.id)
export const badgeColors = {
  new: 'neutral',
  pause: 'warning',
  success: 'success',
  complete: 'success',
  deliver: 'warning',
  deleted: 'error',
  in_progress: 'primary',
  open: 'warning',
  fulfilled: 'success',
  partial: 'warning',
  canceled: 'error',
  ok: 'success',
  missing: 'warning',
  not_required: 'neutral',
  pending: 'warning',
  full: 'success',
}

/**
 * @param {string|null|undefined} id — идентификатор статуса
 * @returns {string} цвет для UBadge
 */
export function getBadgeColor(id) {
  if (!id) return 'neutral'
  return badgeColors[id] || 'primary'
}

/**
 * Переводит статус по его id через i18n-ключи `statuses.<id>`.
 * Если перевода нет — возвращает fallback (title из API).
 *
 * @param {Function} t — функция перевода из useI18n()
 * @param {string|null|undefined} id — идентификатор статуса
 * @param {string} [fallback] — запасное значение (обычно title из API)
 * @returns {string}
 */
export function statusLabel(t, id, fallback = '') {
  if (!id) return fallback
  const key = `statuses.${id}`
  // missingWarn: false — чтобы не спамить intlify-варнингами,
  // когда значение уже переведено (напр. title из API — «Новый»)
  const translated = t(key, undefined, { missingWarn: false })
  return translated && translated !== key ? translated : fallback
}

/**
 * @param {Array<{key:string}>} columns — массив колонок
 * @returns {{key:string}|undefined} колонка статуса
 */
export function findStatusCol(columns) {
  if (!columns) return undefined
  return columns.find((c) =>
    c.key === 'status'
    || c.key === 'statusUuid'
    || c.key === 'boardStatus'
    || c.key === 'boardStatusUuid'
    || c.key.endsWith('_status')
    || c.key === 'state'
  )
}

/**
 * Реактивный хелпер: получает лейбл, id и цвет статуса для карточки.
 *
 * @param {import('vue').Ref<Record<string,any>>} item — строка данных
 * @param {import('vue').Ref<Array<{key:string}>>} columns — колонки
 * @param {{ scheme?: import('vue').ComputedRef, lookupStore?: import('vue').ComputedRef }} [opts]
 * @returns {{ statusCol, statusId, statusValue, statusColor }}
 */
export function useStatusBadge(item, columns, opts = {}) {
  const { t } = useI18n()
  const statusCol = computed(() => findStatusCol(columns.value))

  // Разрешает объект статуса: напрямую из item или через lookup/lookupVariable.
  // Например, для lead — boardStatusUuid резолвится из boardWithStatus
  // через lookupVariable { field, condition, name: 'boardStatuses' }.
  const statusItem = computed(() => {
    if (!statusCol.value) return null
    const raw = item.value?.[statusCol.value.key]
    if (!raw) return null
    if (typeof raw === 'object') return raw

    if (opts.scheme && opts.lookupStore) {
      const schemeItem = opts.scheme.getTableScheme?.(statusCol.value.key)
      const lookupKey = schemeItem?.props?.lookup
      if (lookupKey) {
        const lookupVariable = schemeItem?.props?.lookupVariable
        const variable = lookupVariable?.value ?? lookupVariable
        const source = opts.lookupStore.getLookup(lookupKey)

        if (variable && source) {
          const { field, condition, name } = variable
          const found = Array.isArray(source)
            ? source.find((el) => el[field] === condition)
            : null
          const list = Array.isArray(found?.[name]) ? found[name] : []
          return list.find((el) => el.id === raw) || null
        }
      }
    }
    return null
  })

  const statusId = computed(() => {
    const s = statusItem.value
    if (s) return s?.code || s?.id
    if (!statusCol.value) return null
    const raw = item.value?.[statusCol.value.key]
    if (!raw) return null
    return typeof raw === 'object' ? raw.id : raw
  })

  const statusValue = computed(() => {
    if (!statusCol.value) return ''
    const raw = item.value?.[statusCol.value.key]
    if (!raw) return ''

    // статус пришёл объектом { id, title }
    if (typeof raw === 'object') {
      const fallback = raw?.title || raw?.name || raw?.code || ''
      return statusLabel(t, raw?.id, fallback) || String(raw)
    }

    // статус — uuid, найденный через lookup/lookupVariable
    if (statusItem.value) {
      const fallback =
        statusItem.value?.title ||
        statusItem.value?.name ||
        statusItem.value?.code ||
        ''
      return statusLabel(t, statusId.value, fallback) || fallback
    }

    // статус — код/uuid, резолвим через lookup как в MobileCell
    if (opts.scheme && opts.lookupStore) {
      const schemeItem = opts.scheme.getTableScheme?.(statusCol.value.key)
      const lookupKey = schemeItem?.props?.lookup
      if (lookupKey) {
        const resolved = opts.lookupStore.getLookupItemPerId(lookupKey, raw)
        if (resolved) return statusLabel(t, resolved) || resolved
      }
    }

    // статус — код без lookup
    return statusLabel(t, raw) || String(raw)
  })

  const statusColor = computed(() => getBadgeColor(statusId.value))

  return { statusCol, statusId, statusValue, statusColor }
}
